import React, { useEffect } from 'react';
import { Button, Space, Modal, Form, Input, Select, Card } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type { BannerAddDto, BannerUpdateDto } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import { MultiImageUpload } from '@/page/Cms/ProductManagement/productShared';
import styles from '@/components/CommonTable/index.module.less';

/** 与 sinolinkway_app 前端路由保持一致，供轮播链接下拉选择 */
const APP_LINK_OPTIONS = [
  { label: '不跳转', value: '' },
  { label: '首页', value: '/' },
  { label: '产品列表', value: '/products' },
  { label: '关于我们', value: '/about' },
  { label: '联系我们', value: '/contact' },
  { label: '新闻列表', value: '/news' },
  { label: '常见问题', value: '/faq' },
  { label: '自定义链接', value: '__custom__' },
];

const SORT_ORDER_OPTIONS = Array.from({ length: 30 }, (_, i) => ({
  label: String(i + 1),
  value: i + 1,
}));

type BannerRow = Record<string, unknown> & {
  id: number;
  title?: string;
  image?: string;
  imageMobile?: string;
  imageDesktop?: string;
  link?: string;
  sortOrder?: number;
};

const normalizeRows = (rows: unknown[]): BannerRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return { ...item, ...data, id: (data.id as number) ?? (item.id as number) } as BannerRow;
  });
};

const singleImageValueProps = { getValueFromEvent: (urls: string[]) => urls?.[0], getValueProps: (v: string) => ({ value: v ? [v] : [] }) };

const BannerManagement: React.FC = () => {
  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await Service.ycBannerControllerGet(pn, rn, custom);
    const raw = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data;
    const rows = raw?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: raw?.count ?? 0,
        pn: raw?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    fetchData,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<BannerRow>({
    fetchData: fetchApi,
    deleteData: async (id) => { await Service.ycBannerControllerDelete(id); },
    errorMessage: '获取轮播列表失败',
    searchOptions: { fields: ['title', 'link'] },
  });

  const {
    visible,
    editingItem,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<BannerRow>({
    createData: async (raw) => {
      const link = raw.link === '__custom__' ? (raw.linkCustom as string) : (raw.link as string);
      const payload: BannerAddDto = {
        title: raw.title as string,
        image: raw.image as string,
        imageMobile: raw.imageMobile as string,
        imageDesktop: raw.imageDesktop as string,
        link,
        sortOrder: raw.sortOrder as number,
      };
      await Service.ycBannerControllerAdd(payload);
    },
    updateData: async (raw) => {
      const link = raw.link === '__custom__' ? (raw.linkCustom as string) : (raw.link as string);
      const payload: BannerUpdateDto = {
        id: (raw.id as number)!,
        title: raw.title as string,
        image: raw.image as string,
        imageMobile: raw.imageMobile as string,
        imageDesktop: raw.imageDesktop as string,
        link,
        sortOrder: raw.sortOrder as number,
      };
      await Service.ycBannerControllerPut(payload);
    },
  });

  useEffect(() => {
    if (!visible || !editingItem) return;
    const link = editingItem.link as string | undefined;
    const isPreset = link != null && APP_LINK_OPTIONS.some((o) => o.value !== '__custom__' && o.value === link);
    form.setFieldsValue({
      ...editingItem,
      link: isPreset ? link : (link ? '__custom__' : ''),
      linkCustom: link && !isPreset ? link : undefined,
    });
  }, [visible, editingItem, form]);

  const onModalOk = async () => {
    const ok = await handleOk();
    if (ok) fetchData();
  };

  const columns: ColumnsType<BannerRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    createLongTextColumn('标题', 'title', 12),
    createLongTextColumn('图片', 'image', 15),
    createLongTextColumn('链接', 'link', 15),
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  const stats: DataStat[] = [
    { title: '轮播总数', value: pagination.total, prefix: <PictureOutlined />, valueStyle: { color: '#3f8600' } },
    { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
  ];

  const actionButtons: ActionButton<BannerRow>[] = [
    { key: 'edit', label: '编辑', icon: <EditOutlined />, onClick: handleEdit },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record.id),
      confirm: { title: '确定删除该轮播？', okText: '确定', cancelText: '取消' },
    },
  ];

  const linkValue = Form.useWatch('link', form);

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增轮播
            </Button>
            <SearchBar search={search} placeholder="搜索标题、链接" width={300} loading={loading} />
          </Space>
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
      <Modal title={editingItem ? '编辑轮播' : '新增轮播'} open={visible} onOk={onModalOk} onCancel={handleCancel} width={560}>
        <Form form={form} layout="vertical" initialValues={{ link: '' }}>
          <Form.Item name="title" label="标题">
            <Input placeholder="轮播标题" />
          </Form.Item>
          <Form.Item
            name="image"
            label="图片"
            required
            rules={[{ required: true, message: '请上传图片' }]}
            extra="前端通用展示（未区分设备时使用）。推荐 1920×640（3:1），与 PC 端轮播区固定比例一致，保证图片展示完整。"
            {...singleImageValueProps}
          >
            <MultiImageUpload maxCount={1} />
          </Form.Item>
          <Form.Item
            name="imageMobile"
            label="移动端图片"
            extra="前端 <768px 区域使用，固定比例 720×556（约 1.29:1）。请按此比例上传，保证图片展示完整。"
            {...singleImageValueProps}
          >
            <MultiImageUpload maxCount={1} />
          </Form.Item>
          <Form.Item
            name="imageDesktop"
            label="桌面端图片"
            extra="前端 ≥768px 区域使用，固定比例 1920×640（3:1）。请按此比例上传，保证图片展示完整。"
            {...singleImageValueProps}
          >
            <MultiImageUpload maxCount={1} />
          </Form.Item>
          <Form.Item name="link" label="链接跳转">
            <Select options={APP_LINK_OPTIONS} placeholder="选择跳转页面（默认不跳转）" />
          </Form.Item>
          {linkValue === '__custom__' && (
            <Form.Item name="linkCustom" label="自定义链接" rules={[{ required: true, message: '请输入链接' }]}>
              <Input placeholder="如 /custom 或 https://..." />
            </Form.Item>
          )}
          <Form.Item name="sortOrder" label="排序" required rules={[{ required: true, message: '请选择排序' }]}>
            <Select options={SORT_ORDER_OPTIONS} placeholder="选择排序" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BannerManagement;
